import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api';
import { Restaurant, MenuItem, DailyLunch, LunchOrder, ApiError } from '../types';
import { UtensilsCrossed, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [todaysLunch, setTodaysLunch] = useState<DailyLunch | null>(null);
  const [myOrder, setMyOrder] = useState<LunchOrder | null>(null);
  const [myOrders, setMyOrders] = useState<LunchOrder[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (selectedRestaurant) loadMenuItems(selectedRestaurant);
    else { setMenuItems([]); setSelectedMenuItem(''); }
  }, [selectedRestaurant]);

  const loadData = async () => {
    try {
      setLoading(true); setError('');
      if (isAdmin) {
        const [lunchData, restaurantsData, allOrders] = await Promise.all([
          apiClient.getTodaysLunch().catch(() => null),
          apiClient.getRestaurants(),
          apiClient.getMyOrders().catch(() => []),
        ]);
        setTodaysLunch(lunchData);
        setRestaurants(restaurantsData);
        const todaysOrders = allOrders.filter(o => o.lunchDate === today && o.status === 'active');
        setMyOrders(todaysOrders);
        setMyOrder(todaysOrders.length > 0 ? todaysOrders[0] : null);
      } else {
        const [lunchData, restaurantsData, orderData] = await Promise.all([
          apiClient.getTodaysLunch().catch(() => null),
          apiClient.getRestaurants(),
          apiClient.getMyOrder(today).catch(() => null),
        ]);
        setTodaysLunch(lunchData);
        setRestaurants(restaurantsData);
        setMyOrder(orderData);
        if (orderData?.status === 'active') {
          setSelectedRestaurant(orderData.restaurantId);
          setSelectedMenuItem(orderData.menuItemId);
        }
      }
    } catch (err) {
      setError((err as ApiError).message || t('dashboard.errorLoad'));
    } finally { setLoading(false); }
  };

  const loadMenuItems = async (restaurantId: string) => {
    try {
      const items = await apiClient.getMenuItems(restaurantId);
      setMenuItems(items || []);
    } catch (err) {
      setError((err as ApiError).message || t('dashboard.errorMenuItems'));
      setMenuItems([]);
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedRestaurant || !selectedMenuItem) { setError(t('dashboard.errorSelect')); return; }
    try {
      setSubmitting(true); setError(''); setSuccess('');
      if (isAdmin) {
        await apiClient.createOrder({ date: today, restaurantId: selectedRestaurant, menuItemId: selectedMenuItem });
        setSuccess(t('dashboard.successAdd'));
        setSelectedRestaurant(''); setSelectedMenuItem(''); setMenuItems([]);
      } else {
        if (myOrder?.status === 'active') {
          await apiClient.updateOrder(today, { restaurantId: selectedRestaurant, menuItemId: selectedMenuItem });
          setSuccess(t('dashboard.successUpdate'));
        } else {
          await apiClient.createOrder({ date: today, restaurantId: selectedRestaurant, menuItemId: selectedMenuItem });
          setSuccess(t('dashboard.successCreate'));
        }
      }
      await loadData();
    } catch (err) {
      setError((err as ApiError).message || t('dashboard.errorOrder'));
    } finally { setSubmitting(false); }
  };

  const handleCancelOrder = async (orderId?: string) => {
    if (!confirm(t('dashboard.cancelConfirm'))) return;
    try {
      setSubmitting(true); setError(''); setSuccess('');
      await apiClient.cancelOrder(today);
      setSuccess(t('dashboard.successCancel'));
      if (!isAdmin) { setSelectedRestaurant(''); setSelectedMenuItem(''); setMenuItems([]); }
      await loadData();
    } catch (err) {
      setError((err as ApiError).message || t('dashboard.errorCancel'));
    } finally { setSubmitting(false); }
  };

  const isWithinOrderingHours = () => {
    if (!user?.country) return false;
    const tz = user.country === 'ARG' ? 'America/Argentina/Buenos_Aires'
              : user.country === 'CHL' ? 'America/Santiago' : 'America/Bogota';
    const [hour] = new Date().toLocaleString('en-US', { timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit' })
      .split(':').map(Number);
    return hour >= 8 && hour < 18;
  };

  const withinHours  = isWithinOrderingHours();
  const isLunchOpen  = (!todaysLunch || todaysLunch?.status === 'open') && withinHours;
  const isLunchClosed = !withinHours || todaysLunch?.status === 'closed' || todaysLunch?.status === 'archived';

  const countryLabel = () => {
    if (user?.country === 'ARG') return t('common.argentina');
    if (user?.country === 'CHL') return t('common.chile');
    return t('common.colombia');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-1">
          {t('dashboard.welcome', { name: user?.name })}
        </h1>
        <p className="text-muted-foreground">
          {countryLabel()} · {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Status Banner */}
      <div className={cn(
        'rounded-lg p-4 flex items-center gap-3 border',
        isLunchOpen ? 'bg-success/10 border-success/20' : 'bg-destructive/10 border-destructive/20',
      )}>
        {isLunchOpen ? (
          <>
            <CheckCircle className="w-6 h-6 text-success flex-shrink-0" />
            <div>
              <p className="font-semibold text-success">{t('dashboard.ordersOpen')}</p>
              <p className="text-sm text-success/80">{t('dashboard.ordersOpenDesc')}</p>
            </div>
          </>
        ) : (
          <>
            <XCircle className="w-6 h-6 text-destructive flex-shrink-0" />
            <div>
              <p className="font-semibold text-destructive">{t('dashboard.ordersClosed')}</p>
              <p className="text-sm text-destructive/80">
                {!isWithinOrderingHours() ? t('dashboard.ordersClosedHours') : t('dashboard.ordersClosedDay')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <p className="text-success text-sm">{success}</p>
        </div>
      )}

      {/* Current Order(s) */}
      {isAdmin && myOrders.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <UtensilsCrossed className="w-5 h-5 text-primary" />
              {t('dashboard.myOrdersToday')}
              <Badge variant="default" className="ml-1">{myOrders.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {myOrders.map((order) => (
              <div key={order.orderId} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">{t('common.restaurant')}</p>
                    <p className="font-medium text-foreground">{order.restaurantName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">{t('common.menu')}</p>
                    <p className="font-medium text-foreground">{order.menuItemName}</p>
                  </div>
                </div>
                {isLunchOpen && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleCancelOrder(order.orderId)}
                    disabled={submitting}
                    className="ml-4 border-destructive text-destructive hover:bg-destructive/10"
                  >
                    {t('common.cancel')}
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ) : (
        !isAdmin && myOrder?.status === 'active' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <UtensilsCrossed className="w-5 h-5 text-primary" />
                {t('dashboard.myOrderToday')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t('common.restaurant')}</p>
                  <p className="font-medium text-foreground">{myOrder.restaurantName}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t('common.menu')}</p>
                  <p className="font-medium text-foreground">{myOrder.menuItemName}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Order Form */}
      {isLunchOpen && (
        <Card>
          <CardHeader>
            <CardTitle>
              {isAdmin ? t('dashboard.addOrder')
                : myOrder?.status === 'active' ? t('dashboard.modifyOrder')
                : t('dashboard.confirmOrder')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('dashboard.selectRestaurant')}
              </label>
              <select
                value={selectedRestaurant}
                onChange={(e) => setSelectedRestaurant(e.target.value)}
                disabled={submitting}
                className={cn(
                  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200',
                )}
              >
                <option value="">{t('dashboard.selectRestaurantPh')}</option>
                {restaurants.map((r) => (
                  <option key={r.restaurantId} value={r.restaurantId}>{r.name}</option>
                ))}
              </select>
            </div>

            {selectedRestaurant && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('dashboard.selectMenu')}
                </label>
                {!menuItems?.length ? (
                  <p className="text-muted-foreground text-sm">{t('dashboard.noMenus')}</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {menuItems.map((item) => (
                      <button
                        key={item.itemId}
                        onClick={() => setSelectedMenuItem(item.itemId)}
                        disabled={submitting}
                        className={cn(
                          'p-4 border rounded-lg text-left transition-colors duration-150',
                          selectedMenuItem === item.itemId
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50',
                        )}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground">{item.name}</p>
                          {item.isCeliac && <Badge variant="success">{t('common.celiac')}</Badge>}
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSubmitOrder}
                disabled={!selectedRestaurant || !selectedMenuItem || submitting}
                className="flex-1"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isAdmin ? t('dashboard.addOrder')
                  : myOrder?.status === 'active' ? t('dashboard.updateOrder')
                  : t('dashboard.confirmOrder')}
              </Button>
              {!isAdmin && myOrder?.status === 'active' && (
                <Button
                  variant="outline"
                  onClick={() => handleCancelOrder()}
                  disabled={submitting}
                  className="border-destructive text-destructive hover:bg-destructive/10"
                >
                  {t('dashboard.cancelOrder')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Closed empty state */}
      {isLunchClosed && !myOrder && (
        <Card>
          <CardContent className="p-12 text-center">
            <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">{t('dashboard.closedTitle')}</h3>
            <p className="text-muted-foreground">{t('dashboard.closedDesc')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
